<template>
    <nav class="mainnav">
        <div class="logocontainer" v-if="isWorkflowportal && isMobile">
            <Logo />
        </div>
        <div class="top">
            <template v-for="navigationgroup in filter_list(navigationgroupList)">
                <div class="navGroup">
                <span class="nav-header" v-if="navigationgroup.title">
                    <h3>{{ trans(navigationgroup.title) }}</h3>
                </span>
                <ul class="nav navbar-nav">
                    <li v-for="button in filter_buttons(navigationgroup.data)">
                        <button :class="{ active: active(button.name), subactive: subactive(button.name, 0), hassubs: hasSubs(button) }" @click.prevent="handleClick(button.name)">
                            <Icon :name="button.icon" />
                            <span class="title">
                                {{ trans(button.title) }}
                                <Icon v-if="hasSubs(button)" name="angle" />
                            </span>
                        </button>
                        <ul class="nav navbar-nav" v-if="button?.data?.length && active(button.name)">
                            <li v-for="subbutton in filter_buttons(button.data)">
                                <button :class="{ active: active(subbutton.name), subactive: subactive(subbutton.name, 1), hassubs: hasSubs(subbutton) }" @click.prevent="handleClick(subbutton.name)">
                                    <Icon :name="subbutton.icon ?? 'dot'" />
                                    <span class="title">
                                        {{ trans(subbutton.title) }}
                                        <Icon v-if="hasSubs(subbutton)" name="angle" />
                                    </span>
                                </button>
                                <ul class="nav navbar-nav" v-if="subbutton?.data?.length && active(subbutton.name)">
                                    <li v-for="subsubbutton in filter_buttons(subbutton.data)">
                                        <button :class="{ active: active(subsubbutton.name), subactive: subactive(subsubbutton.name, 2) }" @click.prevent="handleClick(subsubbutton.name)">
                                            <Ico :name="subsubbutton.icon ?? 'dot'" />
                                            <span class="title">{{ trans(subsubbutton.title) }}</span>
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                </ul>
                </div>
            </template>
        </div>
        <div class="bottom" v-if="isWorkflowportal">
<!--            <button type="button" @click.prevent="toggleMode">-->
<!--                <Icon name="darkmode" />-->
<!--                <span class="title">{{ textMode }}</span>-->
<!--            </button>-->
            <notificationDropdown />
            <button class="logout" type="button" @click="handleLogout">
                <Icon name="logout" />
                <span class="title">{{ trans("Uitloggen") }}</span>
            </button>
        </div>
        <notificationDropdown v-if="!isWorkflowportal" />
    </nav>
</template>

<script>
import {defineComponent, provide, ref, computed, inject} from "vue";
import { useRouter, useRoute } from "vue-router";

// Components
import Icon from "components/icon";
import notificationDropdown from "components/notification/dropdown";


// Elements
import Logo from "layout/general/logo";

// Helpers
import trans from "helpers/_translation";
import { isMobile } from "helpers/_device";

import { env } from "config";
import { contact, value, unauthorized } from "store/portal";
import { view_visible } from "store/view";
import { getSetting, storeSetting, matchSetting } from 'store/settings';

import { clearCache } from "helpers/_cache";

// Services
import { destroyTokens } from "services/JwtService";


export default defineComponent({
    name: "navigationElement",
    components: {
        Logo,
        Icon,
        notificationDropdown
    },
    setup() {
        const isWorkflowportal = inject("isWorkflowportal");

        const handleClick = inject("handleClick");

        const route = useRoute();
        const router = useRouter();

        const navigationgroupList = computed(() => {
            return env("navigationgroup");
        });

        const handleLogout = () => {
            if (!isWorkflowportal) return;
            destroyTokens();
            clearCache();
            setTimeout(() => {
                router.go("/login");
            }, 100);
        };

        const toggleMode = () => {
            const newValue = matchSetting("layout", "mode", "light") ? "dark" : "light";
            storeSetting("layout", "mode", newValue);
        };
        provide("toggleMode", toggleMode);

        const textMode = computed(() => {
            return matchSetting("layout", "mode", "light") ? trans('Donkere modus') : trans('Lichte modus');
        });

        // Button related code
        const resolveHierarchyMap = (button, map = {}) => {
            // Initialize the array of all sub-names for the current button
            const subNames = [];

            // Recursively process each child and add its name to the subNames array
            if (button?.data?.length) {
                button.data.forEach(subButton => {
                    subNames.push(subButton.name); // Add the child's name
                    resolveHierarchyMap(subButton, map); // Recursive call to process the child
                    subNames.push(...map[subButton.name]); // Add all the child's descendants
                });
            }

            // Add the current button to the map
            map[button.name] = subNames;

            return map;
        };

        const hierarchyMap = computed(() => {
            const map = {};
            navigationgroupList.value.forEach(group => {
                group.data.forEach(button => {
                    resolveHierarchyMap(button, map);
                });
            });
            return map;
        });


        const subactive = (name, level = 0) => {
            return computed(() => {
                return (level === 0) && hierarchyMap.value[name]?.includes(route.name) && (route.name !== name);
            }).value;
        };
        const active = (name) => {
            return computed(() => {
                if (route.name === name) return true;
                return subactive(name);
            }).value;
        };


        const filter_buttons = (buttons) => {
            return computed(() => {
                return buttons?.filter(a => {
                    if (a?.data?.length && a.data.some(b => view_visible(b.name))) {
                        // Check for any subitems being visible
                        return true;
                    }
                    if (a?.name) {
                        return view_visible(a.name);
                    }
                    return false;
                });
            }).value;
        };
        const filter_list = (list) => {
            return computed(() => {
                if (!list) return [];
                return list.filter(a => filter_buttons(a.data).length > 0);
            }).value;
        };

        const hasSubs = (button) => {
            return computed(() => {
                return filter_buttons(button.data)?.length > 0;
            }).value;
        };

        return {
            trans,

            isWorkflowportal,
            env,
            value,
            contact,

            isMobile,

            navigationgroupList,

            handleLogout,
            handleClick,

            active,
            subactive,
            hasSubs,

            filter_list,
            filter_buttons,
            
            toggleMode,
            textMode,
        };
    },
});
</script>